import React, { useState, useMemo, useEffect, useRef } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

// --- Stripe Imports ---
import {
  useStripe,
  useElements,
  CardElement,
} from "@stripe/react-stripe-js";

// --- Local Imports ---
import SubmitButton from "../../common/SubmitButton";
import ApplePayButton from "./ApplePayButton";
import TermsPopup from "./termsPopup";
import http from "../../../helpers/http";
import { makeSubsPayment } from "../../../states/actions/fetchCheckout";
import { validateCoupon } from "../../../states/actions/validateCoupon";
import { price_format } from "../../../helpers/helpers";
import { toast } from "react-toastify";
import { TOAST_SETTINGS } from "../../../utils/siteSettings";

// --- CSS for custom button layout ---
const CSS_STYLES = `
/* auto-fit, not a fixed 3 columns: between 576px and ~820px three tracks are
   narrower than "Credit / Debit Card", and .site_btn is white-space: nowrap, so
   the label overflowed its own button. Tracks reflow to 2 then 1 on their own. */
.button-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(17rem, 1fr));
  gap: 1.2rem;
}
/* Backstop for the same problem: let a long label wrap instead of overflowing,
   which means the row height has to grow with it. */
.button-container .site_btn {
  width: 100%;
  min-width: 0;
  height: auto;
  min-height: 4.7rem;
  padding: 0.8rem;
  white-space: normal;
  line-height: 1.2;
}

/* The tab row and the promo field are separate steps — space them apart. */
.checkout-shared-promo {
  margin-top: 2.4rem;
}
/* .site_btn.blank ships a lavender #E0DEF7 border from the base template, which
   reads as a foreign accent next to the green paylink buttons. */
.checkout-shared-promo .site_btn.blank {
  color: #67773f;
  border-color: rgba(103, 119, 63, 0.55);
}

.button-wrapper {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* --- Recommended Stripe-hosted checkout path ----------------------------- */
.paylink {
  margin: 2rem 0;
  padding: 1.8rem;
  background: #f6f8ef;
  border: 1px solid #d8dec6;
  border-radius: 0.8rem;
}
.paylink__badge {
  display: inline-block;
  margin-bottom: 0.8rem;
  padding: 0.35rem 0.7rem;
  color: #fff;
  background: #67773f;
  border-radius: 999px;
  font-family: "Bold";
  font-size: 1.1rem;
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.paylink h6 {
  margin-bottom: 0.6rem;
}
.paylink__description,
.paylink__note {
  font-size: 1.3rem;
  line-height: 1.4;
  color: #5a6047;
  text-align: left;
}
.paylink__description {
  margin-bottom: 1.2rem;
}
.paylink__note {
  margin: 1rem 0 0;
}
.paylink__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.8rem;
}
.paylink__btn {
  min-height: 4.4rem;
  padding: 0 1.4rem;
  font-family: "Bold";
  font-size: 1.3rem;
  line-height: 1;
  color: #67773f;
  background: #fff;
  border: 1.5px solid rgba(103, 119, 63, 0.55);
  border-radius: 0.6rem;
  cursor: pointer;
  transition: all ease 0.25s;
}
.paylink__btn--primary {
  color: #fff;
  background: #67773f;
  border-color: #67773f;
}
.paylink__btn:hover:not(:disabled) {
  opacity: 0.82;
}
.paylink__btn:disabled {
  opacity: 0.5;
  cursor: default;
}
.paylink__btn:focus-visible {
  outline: 2px solid #67773f;
  outline-offset: 2px;
}
.payment-path-divider {
  display: flex;
  align-items: center;
  gap: 1.2rem;
  margin: 2.4rem 0;
  color: #67773f;
  font-family: "Bold";
  font-size: 1.2rem;
}
.payment-path-divider::before,
.payment-path-divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: #d8dec6;
}
.direct-payment-heading {
  margin-bottom: 1.7rem;
}
@media (max-width: 575px) {
  .paylink__actions {
    align-items: stretch;
    flex-direction: column;
  }
  .paylink__btn {
    width: 100%;
  }
}
`;

/**
 * Custom hook to provide memoized styling options for the Stripe CardElement.
 * This prevents the options object from being recreated on every render.
 * @returns {object} - Stripe Element options.
 */
const useOptions = () => {
  const options = useMemo(
    () => ({
      hidePostalCode: true,
      style: {
        base: {
          display: "block",
          width: "100%",
          height: "5.3rem",
          fontFamily: "'Red Hat Display', sans-serif",
          fontWeight: "500",
          color: "#061237",
          background: "#fff",
          "text-align": "left",
          padding: "0.6rem 1.4rem",
          "::placeholder": {
            color: "#130a2952",
            fontSize: "15px",
          },
        },
        invalid: {
          color: "#9e2146",
        },
      },
    }),
    []
  );

  return options;
};

const formatPromoCodeMessage = (
  message,
  fallback = "That Promo Code isn't valid."
) =>
  (message || fallback)
    .replace(/coupon code/gi, "Promo Code")
    .replace(/\bcoupon\b/gi, "Promo Code");

/**
 * Checkout Form Component
 *
 * Handles user input for billing information, Stripe payment processing,
 * and subscription creation.
 */
const Form = ({
  content,
  plan,
  subscription,
  terms_conditions,
  subscription_agreement,
}) => {
  // --- Hooks ---
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const options = useOptions();
  const { plan_id } = useParams();
  const [searchParams] = useSearchParams();
  // Event signup (/event?code=XXXX) lands here as /checkout/:plan_id?code=XXXX.
  // The code is pre-filled and applied once on load, so the attendee only
  // has to enter a card. Without the param nothing changes.
  const urlPromoCode = (searchParams.get("code") || "").trim();

  // --- Redux State ---
  const isFormProcessing = useSelector(
    (state) => state.fetchCheckout.isFormProcessing
  );
  const subscriptionPaymentStatus = useSelector(
    (state) => state.fetchCheckout.subscriptionPaymentStatus
  );

  // --- Component State ---
  const [formData, setFormData] = useState({
    name_on_card: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    coupon_code: urlPromoCode,
  });
  const [formErrors, setFormErrors] = useState({});
  const [cardError, setCardError] = useState(null);
  const [cardLoading, setCardLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [achStep, setAchStep] = useState("idle");
  const [achError, setAchError] = useState(null);
  const [applePayAvailable, setApplePayAvailable] = useState(null);
  const [termsPopup, setTermsPopup] = useState(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);

  // Coupon preview state. `appliedCoupon` is set after a successful
  // /validate-coupon call and holds the discount data the backend returned.
  // Once applied, the coupon input is locked until the user clicks Remove.
  // `couponApplying` is the loading state for the Apply button.
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponApplying, setCouponApplying] = useState(false);

  // Rep-generated payment link. Lets the CUSTOMER enter their own card or bank
  // details on Stripe's hosted page, instead of the rep typing them into the form
  // above. "Copy" is for a managed signup done over the phone; "Open" is for a
  // self-signup where the customer is sitting right there.
  const [linkLoading, setLinkLoading] = useState(false);
  const paymentBusy =
    linkLoading || cardLoading || isFormProcessing || couponApplying;

  // A Promo Code that brings the total to $0 is a free trial. Those start on Stripe's
  // hosted Checkout, which verifies the card with the issuer before the trial begins.
  const isFreeTrial =
    appliedCoupon?.total_after_discount != null &&
    Number(appliedCoupon.total_after_discount) <= 0;
  const linkInFlightRef = useRef(false);

  // --- Effect to close the terms popup when terms are accepted ---
  useEffect(() => {
    if (isTermsAccepted) {
      setTermsPopup(false);
    }
  }, [isTermsAccepted]);

  // Reset ACH loading state only when the payment thunk actually fails.
  // Success dispatches before the delayed dashboard redirect, so loading=false
  // alone is not a reliable failure signal.
  useEffect(() => {
    if (subscriptionPaymentStatus === "failed" && achStep === "pending") {
      setCardLoading(false);
      setAchStep("error");
      setAchError("Payment submission failed. Please try again.");
    }
  }, [subscriptionPaymentStatus, achStep]);

  /**
   * Handles changes in form input fields and updates the state.
   * @param {React.ChangeEvent<HTMLInputElement>} e - The input change event.
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    // Clear the specific error when the user starts typing
    if (formErrors[name]) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        [name]: null,
      }));
    }
    // If the user is editing the coupon field after applying one, clear the
    // applied discount preview so it can't show stale info for a different
    // code than what's in the input.
    if (name === "coupon_code" && appliedCoupon) {
      setAppliedCoupon(null);
    }
  };

  /**
   * Validates the current Promo Code via the backend and, on success, stores
   * the discount preview so the user can see the total they'll be charged
   * before the card is run.
   */
  const handleApplyCoupon = async () => {
    const code = formData.coupon_code.trim();
    if (!code || couponApplying) return;
    setCouponApplying(true);
    // Clear any prior errors / previews so the UI reflects the new attempt.
    setFormErrors((prev) => ({ ...prev, coupon_code: null }));
    setAppliedCoupon(null);
    try {
      const data = await dispatch(
        validateCoupon({ coupon_code: code, plan_id })
      );
      if (data?.status === 1) {
        setAppliedCoupon(data);
      } else {
        setFormErrors((prev) => ({
          ...prev,
          coupon_code: formatPromoCodeMessage(data?.msg),
        }));
      }
    } catch (err) {
      setFormErrors((prev) => ({
        ...prev,
        coupon_code: formatPromoCodeMessage(
          err?.response?.data?.msg ||
            err?.message ||
            "Network error while validating the Promo Code."
        ),
      }));
    } finally {
      setCouponApplying(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setFormErrors((prev) => ({ ...prev, coupon_code: null }));
    setFormData((prev) => ({ ...prev, coupon_code: "" }));
  };

  // Apply a ?code= Promo Code once, on first render (formData already holds it).
  const urlPromoAppliedRef = useRef(false);
  useEffect(() => {
    if (!urlPromoCode || urlPromoAppliedRef.current) return;
    urlPromoAppliedRef.current = true;
    handleApplyCoupon();
  }, []);

  /**
   * Validates the form data before submission.
   * @returns {boolean} - True if the form is valid, false otherwise.
   */
  const validateForm = () => {
    const errors = {};
    if (!formData.name_on_card.trim()) {
      errors.name_on_card =
        paymentMethod === "ach"
          ? "Account holder name is required."
          : "Card holder name is required.";
    }
    if (!formData.address.trim()) errors.address = "Address is required.";
    if (!formData.city.trim()) errors.city = "City is required.";
    if (!formData.state.trim()) errors.state = "State is required.";
    if (!formData.zip_code.trim()) errors.zip_code = "Zip code is required.";

    setFormErrors(errors);
    // Return true if the errors object is empty
    return Object.keys(errors).length === 0;
  };

  /**
   * Decide whether a backend rejection of /create-payment-intent is actually
   * about the Promo Code (so we can show an inline error under that field
   * instead of dumping everything into the generic card-error banner).
   *
   * Defensive: matches whether the backend returns a structured code
   * (`code: "invalid_coupon"` etc.), an explicit field marker
   * (`error_field: "coupon_code"`), OR just a human-readable msg whose text
   * mentions coupon/promo/discount. Stays useful even if the backend
   * response shape changes.
   */
  const isCouponError = (data) => {
    if (!data) return false;
    const code = (data.code || "").toLowerCase();
    if (
      code === "invalid_coupon" ||
      code === "coupon_invalid" ||
      code === "coupon_expired" ||
      code === "coupon_not_found"
    )
      return true;
    if (data.error_field === "coupon_code") return true;
    const msg = (data.msg || "").toLowerCase();
    return (
      /coupon/.test(msg) ||
      /promo.*code/.test(msg) ||
      /invalid.*code/.test(msg) ||
      /discount.*code/.test(msg)
    );
  };

  /**
   * Handles the final form submission after validation and terms acceptance.
   * @param {React.FormEvent<HTMLFormElement>} e - The form submission event.
   */
  const handleFormSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission
    if (linkLoading) {
      return;
    }
    if (!validateForm()) {
      return; // Stop if validation fails
    }
    if (!isTermsAccepted) {
      setTermsPopup(true);
      return;
    }
    // Without this, a typed-but-unapplied free-trial code would slip onto the card form.
    if (formData.coupon_code.trim() && !appliedCoupon) {
      setFormErrors((prev) => ({
        ...prev,
        coupon_code: "Apply the Promo Code before submitting.",
      }));
      return;
    }
    setCardLoading(true);
    if (isFreeTrial) {
      await startHostedFreeTrial();
      return;
    }
    const dataWithAction = { ...formData, action: searchParams.get("action") };
    if (paymentMethod === "ach") {
      await handleSubmitAch(dataWithAction);
    } else if (paymentMethod === "apple_pay") {
      setCardError("Use the Apple Pay button above to complete checkout.");
      setCardLoading(false);
    } else {
      await handleSubmitCard(dataWithAction);
    }
  };

  /**
   * Handles the click on the "Proceed" button. Validates the form
   * and opens the terms and conditions popup if the form is valid.
   */
  const handleProceedToTerms = () => {
    if (validateForm()) {
      setTermsPopup(true);
    }
  };

  /**
   * Handles changes in the Stripe CardElement and sets card-specific errors.
   * @param {object} ev - The Stripe change event object.
   */
  const handleCardDetailsChange = (ev) => {
    setCardError(ev.error ? ev.error.message : null);
  };

  /**
   * Processes the payment with Stripe.
   * 1. Creates a PaymentMethod.
   * 2. Sends the PaymentMethod ID and form data to the backend.
   * 3. Backend creates a Stripe Subscription and returns a client secret.
   * 4. Frontend confirms the card setup using the client secret.
   * 5. On success, calls chargePayment to finalize the process in the local database.
   * @param {object} postData - The billing and form data.
   */
  const handleSubmitCard = async (postData) => {
    setCardError(null);

    if (!stripe || !elements) {
      console.error("Stripe.js has not loaded yet.");
      setCardLoading(false);
      return;
    }

    const cardElement = elements.getElement(CardElement);

    // We collect the full billing address on this form but used to drop it on the
    // floor, so every PaymentMethod reached the issuer with
    // address_line1_check / address_postal_code_check = null. Issuers — American
    // Express especially — treat a card-verification attempt carrying no AVS data
    // as high risk and answer `do_not_honor`. Send what the customer already typed.
    // The CardElement runs with hidePostalCode, so the ZIP comes from our own field.
    const billing_details = {
      name: postData.name_on_card,
      address: {
        line1: postData.address,
        city: postData.city,
        state: postData.state,
        postal_code: postData.zip_code,
        country: "US",
      },
    };

    try {
      // Step 1: Create a PaymentMethod
      const { error: paymentMethodError, paymentMethod } =
        await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
          billing_details,
        });

      if (paymentMethodError) {
        setCardError(paymentMethodError.message);
        setCardLoading(false);
        return;
      }

      // Step 2: Send data to the backend
      const backendFormData = new FormData();
      Object.keys(postData).forEach((key) => {
        backendFormData.append(key, postData[key]);
      });
      backendFormData.append("payment_method", paymentMethod.id);
      backendFormData.append("plan_id", plan_id);
      backendFormData.append("token", localStorage.getItem("authToken"));
      backendFormData.append("host", window.location.hostname);

      const { data: responseData } = await http.post(
        `/create-payment-intent`,
        backendFormData
      );

      // Handle backend response
      if (responseData?.status === 1) {
        const { subscriptionId, clientSecret, customerId } = responseData;

        // Step 3 & 4: Confirm the card setup on the frontend
        const { error: setupError, paymentIntent } =
          await stripe.confirmCardSetup(clientSecret, {
            payment_method: { card: cardElement, billing_details },
          });

        if (setupError) {
          setCardError(setupError.message);
          setCardLoading(false);
          return;
        }

        // Step 5: Finalize payment by sending confirmation to the backend
        chargePayment(
          postData,
          subscriptionId,
          customerId,
          paymentIntent,
          paymentMethod.id
        );
      } else {
        // If the rejection is coupon-related, surface it inline under the
        // coupon field so the user can correct it without re-entering card
        // info. Anything else stays in the generic card-error banner.
        if (isCouponError(responseData)) {
          setFormErrors((prev) => ({
            ...prev,
            coupon_code: formatPromoCodeMessage(responseData.msg),
          }));
        } else {
          setCardError(responseData.msg || "An unexpected error occurred.");
        }
        setCardLoading(false);
      }
    } catch (err) {
      // Prefer the server's own message. On a non-2xx the backend still explains
      // itself in `msg`, and falling straight through to err.message is how a real
      // reason ("Your card was declined.") got replaced by axios's bare
      // "Network Error" / "Request failed with status code 500".
      setCardError(
        err?.response?.data?.msg || err.message || "An unexpected error occurred."
      );
      setCardLoading(false);
    }
  };

  const handleSubmitAch = async (postData) => {
    setAchError(null);
    setAchStep("collecting");

    if (!stripe || !elements) {
      setAchStep("error");
      setAchError("Stripe has not loaded yet.");
      setCardLoading(false);
      return;
    }

    try {
      const backendFormData = new FormData();
      Object.keys(postData).forEach((key) => {
        backendFormData.append(key, postData[key]);
      });
      backendFormData.append("payment_method_type", "ach");
      backendFormData.append("plan_id", plan_id);
      backendFormData.append("token", localStorage.getItem("authToken"));
      backendFormData.append("host", window.location.hostname);

      const { data: responseData } = await http.post(
        "/create-payment-intent",
        backendFormData
      );

      if (responseData?.status !== 1) {
        if (isCouponError(responseData)) {
          setFormErrors((prev) => ({
            ...prev,
            coupon_code: formatPromoCodeMessage(responseData.msg),
          }));
        } else {
          setAchError(responseData?.msg || "Unable to start ACH checkout.");
        }
        setAchStep("error");
        setCardLoading(false);
        return;
      }

      const { subscriptionId, clientSecret, customerId, effective_price } =
        responseData;

      const { setupIntent, error: collectError } =
        await stripe.collectBankAccountForSetup({
          clientSecret,
          params: {
            payment_method_type: "us_bank_account",
            payment_method_data: {
              billing_details: {
                name: postData.name_on_card,
              },
            },
          },
        });

      if (collectError) {
        setAchError(collectError.message);
        setAchStep("error");
        setCardLoading(false);
        return;
      }

      if (setupIntent.status === "requires_payment_method") {
        setAchStep("idle");
        setCardLoading(false);
        return;
      }

      setAchStep("confirming");
      const { setupIntent: confirmedIntent, error: confirmError } =
        await stripe.confirmUsBankAccountSetup(clientSecret, {});

      if (confirmError) {
        setAchError(confirmError.message);
        setAchStep("error");
        setCardLoading(false);
        return;
      }

      if (!confirmedIntent?.payment_method) {
        setAchError("Stripe did not return a bank payment method.");
        setAchStep("error");
        setCardLoading(false);
        return;
      }

      setAchStep("pending");
      dispatch(
        makeSubsPayment({
          ...postData,
          subscriptionId,
          customer_id: customerId,
          plan_id,
          paymentIntent: confirmedIntent.id,
          setup_intent_id: confirmedIntent.id,
          payment_method_id: confirmedIntent.payment_method,
          payment_method_type: "ach",
          effective_price,
          host: window.location.hostname,
        })
      );
    } catch (err) {
      setAchError(err.message || "Unable to process ACH payment.");
      setAchStep("error");
      setCardLoading(false);
    }
  };

  /**
   * Dispatches an action to save the subscription details to the backend
   * after a successful Stripe transaction.
   */
  const chargePayment = (
    postData,
    subscriptionId,
    customerId,
    paymentIntent,
    payment_method_id
  ) => {
    const finalData = {
      ...postData,
      subscriptionId,
      customer_id: customerId,
      plan_id,
      paymentIntent: paymentIntent?.id || "",
      payment_method_id,
      host: window.location.hostname,
    };
    dispatch(makeSubsPayment(finalData));
  };

  const validateApplePayPayment = () => {
    if (!validateForm()) {
      return false;
    }

    if (!isTermsAccepted) {
      setTermsPopup(true);
      return false;
    }

    return true;
  };

  const chargeApplePayPayment = (finalData) => {
    return dispatch(makeSubsPayment(finalData));
  };

  /**
   * Handles switching between yearly and monthly plans (if applicable).
   * NOTE: This functionality appears to be commented out in the original JSX,
   * but the handler function is preserved here.
   */
  const handleYearlyButtonClick = async () => {
    try {
      const { data } = await http.post("year-month", { planId: plan.id });
      const actionParam = searchParams.get("action");
      const redirectUrl = actionParam
        ? `/checkout/${data.plan_id}?action=${actionParam}`
        : `/checkout/${data.plan_id}`;
      window.location.href = redirectUrl;
    } catch (error) {
      console.error("Error switching plan:", error);
    }
  };

  /**
   * Create a Stripe-hosted Checkout Session for this plan, Promo Code and payment method.
   * Resolves to the backend's response body.
   */
  const requestCheckoutLink = async () => {
    const { data } = await http.post("create-checkout-link", {
      plan_id: plan_id,
      payment_method_type: paymentMethod === "ach" ? "ach" : "card",
      coupon_code: appliedCoupon ? formData.coupon_code : "",
      host: window.location.hostname,
    });
    return data;
  };

  /**
   * Free trials leave this page for Stripe's hosted Checkout in the same tab. Access is
   * provisioned by the checkout.session.completed webhook, as for any payment link.
   */
  const startHostedFreeTrial = async () => {
    setCardError(null);
    try {
      const data = await requestCheckoutLink();
      if (data?.status && data?.url) {
        window.location.assign(data.url);
        return;
      }
      setCardError(data?.msg || "Could not open Stripe's secure checkout. Please try again.");
    } catch (error) {
      setCardError(
        error?.response?.data?.msg ||
          "Could not open Stripe's secure checkout. Please try again."
      );
    }
    setCardLoading(false);
  };

  /**
   * Ask the backend for a Stripe-hosted Checkout Session and either copy its URL or
   * open it. Nothing is written to our database here — the subscription is provisioned
   * by the checkout.session.completed webhook once Stripe confirms the payment.
   *
   * The price is resolved server-side from the same plan/coupon/ACH rules the form
   * itself uses, so the link always charges what the rep sees on screen. Apple Pay
   * needs no special case: the backend normalises anything that isn't "ach" to card,
   * and Stripe's hosted page offers the wallets automatically.
   */
  const handleGeneratePaymentLink = async (mode) => {
    if (paymentBusy || linkInFlightRef.current) {
      return;
    }
    if (formData.coupon_code.trim() && !appliedCoupon) {
      toast.error("Apply the Promo Code before creating a payment link.", TOAST_SETTINGS);
      return;
    }
    linkInFlightRef.current = true;

    // Browsers only allow window.open inside the user-gesture call stack, and awaiting the
    // request below leaves it — so opening after the response is silently swallowed by popup
    // blockers and the button appears dead. Claim the tab synchronously here, then point it
    // at the URL once we have it.
    //
    // Deliberately no "noopener" feature string: per spec that makes window.open return null,
    // which would defeat the whole point. The back-reference is severed manually below.
    const pendingWindow = mode === "open" ? window.open("", "_blank") : null;

    setLinkLoading(true);
    try {
      const data = await requestCheckoutLink();

      if (!data?.status || !data?.url) {
        pendingWindow?.close();
        toast.error(
          data?.msg || "Could not create the payment link.",
          TOAST_SETTINGS
        );
        return;
      }

      const linkSnapshot = {
        url: data.url,
        // What gets copied and sent. Stripe's own url is ~643 chars and dies if its "#fid…"
        // fragment is damaged in transit, which messaging apps reliably do; short_url is a
        // redirect that hands the browser that fragment instead. Falls back to the raw url so
        // a frontend deployed ahead of the backend still works.
        shareUrl: data.short_url || data.url,
        paymentMethodType: data.payment_method_type,
        effectivePrice: data.effective_price,
        achDiscountApplied: data.ach_discount_applied,
        expiresAt: data.expires_at,
      };
      if (mode === "open") {
        if (pendingWindow) {
          pendingWindow.opener = null; // what "noopener" would have done
          pendingWindow.location.replace(linkSnapshot.url);
        } else {
          // The blocker refused the tab even inside the gesture. Try once more, and if that
          // is refused too, say so — silently doing nothing is the failure being fixed here.
          const opened = window.open(
            linkSnapshot.url,
            "_blank",
            "noopener,noreferrer"
          );
          if (!opened) {
            toast.error(
              "Your browser blocked the payment page. Allow pop-ups for this site, or use \"Copy payment link\".",
              TOAST_SETTINGS
            );
          }
        }
        return;
      }

      try {
        await navigator.clipboard.writeText(linkSnapshot.shareUrl);
        toast.success("Payment link copied.", TOAST_SETTINGS);
      } catch (clipboardError) {
        // Clipboard needs a secure context; with no link shown on screen there is
        // no manual fallback, so point at the button that always works.
        toast.error(
          "Could not copy the link. Use \"Open Stripe\" to pay here instead.",
          TOAST_SETTINGS
        );
      }
    } catch (error) {
      pendingWindow?.close(); // don't strand a blank tab on a failed request
      console.error("Error creating payment link:", error);
      toast.error(
        "Something went wrong while creating the payment link.",
        TOAST_SETTINGS
      );
    } finally {
      setLinkLoading(false);
      linkInFlightRef.current = false;
    }
  };

  return (
    <>
      <form onSubmit={handleFormSubmit}>
        <div className="blk_form">
          <h6>Activate your account</h6>
          <div className="form_blk">
            <div className="check_box_pkgs_choose">
              <input
                type="radio"
                name="package"
                id="billed_mnthly"
                className="hidden"
                defaultChecked
              />
              <label htmlFor="billed_mnthly">
                <span>
                  Billed{" "}
                  {plan?.plan_interval === "month"
                    ? "Monthly"
                    : plan?.plan_interval === "year"
                    ? "Yearly"
                    : ""}
                </span>
                <strong>
                  $ {plan?.price}/
                  {plan?.plan_interval === "month" || plan?.type === "monthly"
                    ? "mo"
                    : "yr"}
                </strong>
              </label>
            </div>
            <br />
            {/* The plan switching UI was commented out in the original code */}
            <style>{CSS_STYLES}</style>
          </div>
        </div>

        {/* --- Payment Method Section --- */}
        <div className="blk_form">
          <h6>Payment method</h6>
          <div className="button-container" style={{ gap: 12 }}>
            <button
              type="button"
              className="site_btn"
              style={{
                flex: 1,
                background: paymentMethod === "card" ? undefined : "#fff",
                color: paymentMethod === "card" ? undefined : "#061237",
              }}
              onClick={() => {
                setPaymentMethod("card");
                setAchError(null);
                setAchStep("idle");
                setCardError(null);
              }}
              disabled={paymentBusy}
            >
              Credit / Debit Card
            </button>
            <button
              type="button"
              className="site_btn"
              style={{
                flex: 1,
                background: paymentMethod === "apple_pay" ? undefined : "#fff",
                color: paymentMethod === "apple_pay" ? undefined : "#061237",
              }}
              onClick={() => {
                setPaymentMethod("apple_pay");
                setAchError(null);
                setAchStep("idle");
                setCardError(null);
              }}
              disabled={paymentBusy}
            >
              Apple Pay
            </button>
            <button
              type="button"
              className="site_btn"
              style={{
                flex: 1,
                background: paymentMethod === "ach" ? undefined : "#fff",
                color: paymentMethod === "ach" ? undefined : "#061237",
              }}
              onClick={() => {
                setPaymentMethod("ach");
                setCardError(null);
                setApplePayAvailable(null);
              }}
              disabled={paymentBusy}
            >
              Bank Account
            </button>
          </div>
          {paymentMethod === "ach" && !appliedCoupon && (
            <p className="validation-error" style={{ color: "#16803c" }}>
              5% ACH discount will be applied at checkout when no Promo Code is active.
            </p>
          )}
          {paymentMethod === "ach" && (
            <p>
              ACH payments usually take 2-4 business days to confirm. Access
              activates after bank confirmation.
            </p>
          )}

          <div className="blk_form checkout-shared-promo">
            <h6>Promo Code</h6>
            <div className="form_blk">
              <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
                <input
                  type="text"
                  name="coupon_code"
                  className="input"
                  placeholder="Enter Promo Code"
                  value={formData.coupon_code}
                  onChange={handleInputChange}
                  disabled={!!appliedCoupon || couponApplying || linkLoading}
                  style={{ flex: 1, minWidth: 0 }}
                />
                {appliedCoupon ? (
                  <button
                    type="button"
                    className="site_btn blank"
                    onClick={handleRemoveCoupon}
                    disabled={linkLoading}
                    style={{ whiteSpace: "nowrap" }}
                  >
                    Remove
                  </button>
                ) : (
                  <button
                    type="button"
                    className="site_btn blank"
                    onClick={handleApplyCoupon}
                    disabled={
                      !formData.coupon_code.trim() ||
                      couponApplying ||
                      linkLoading
                    }
                    style={{ whiteSpace: "nowrap" }}
                  >
                    {couponApplying ? "Applying…" : "Apply"}
                  </button>
                )}
              </div>
              {formErrors.coupon_code && (
                <span className="validation-error">
                  {formErrors.coupon_code}
                </span>
              )}
            </div>

            {appliedCoupon && (
              <div
                style={{
                  marginTop: 12,
                  padding: "12px 14px",
                  background: "#f6f8ef",
                  border: "1px solid #d8dec6",
                  borderRadius: 6,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 14,
                    color: "#5a6047",
                  }}
                >
                  <span>Subtotal</span>
                  <span>{price_format(appliedCoupon.original_price)}</span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 14,
                    color: "#67773F",
                    fontWeight: 600,
                    marginTop: 4,
                  }}
                >
                  <span>
                    Discount
                    {appliedCoupon.discount_label
                      ? ` (${appliedCoupon.discount_label})`
                      : ""}
                  </span>
                  <span>−{price_format(appliedCoupon.discount_value)}</span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 16,
                    fontWeight: 700,
                    color: "#1f2a14",
                    marginTop: 10,
                    paddingTop: 10,
                    borderTop: "1px solid #d8dec6",
                  }}
                >
                  <span>Total payable</span>
                  <span>{price_format(appliedCoupon.total_after_discount)}</span>
                </div>
              </div>
            )}
          </div>

          <div className="paylink">
            <span className="paylink__badge">Recommended</span>
            <h6>Pay securely with Stripe</h6>
            <p className="paylink__description">
              Complete payment on Stripe's secure page. No payment details are
              entered here.
            </p>
            <div className="paylink__actions">
              <button
                type="button"
                className="paylink__btn paylink__btn--primary"
                disabled={paymentBusy}
                onClick={() => handleGeneratePaymentLink("open")}
              >
                {linkLoading ? "Creating…" : "Open Stripe"}
              </button>
              <button
                type="button"
                className="paylink__btn"
                title="Link stays valid for 24 hours"
                disabled={paymentBusy}
                onClick={() => handleGeneratePaymentLink("copy")}
              >
                Copy payment link
              </button>
            </div>
            <p className="paylink__note">
              Payment links remain valid for 24 hours.
            </p>
          </div>
        </div>

        <div className="payment-path-divider" aria-hidden="true">
          <span>OR</span>
        </div>
        <h6 className="checkout_sm_heading direct-payment-heading">
          Pay directly on this page
        </h6>

        {isFreeTrial && (
          <div className="blk_form">
            <h6>Free trial</h6>
            <p>
              You'll confirm your{" "}
              {paymentMethod === "ach" ? "bank account" : "card"} on Stripe's
              secure page next. You won't be charged today.
              {paymentMethod !== "ach" &&
                " Prepaid cards can't be used for free trials."}
            </p>
            {cardError && <p className="validation_error">{cardError}</p>}
          </div>
        )}

        {/* --- Credit Card Section --- */}
        {paymentMethod === "card" && !isFreeTrial && (
          <div className="blk_form date_ex_flex">
            <h6>Credit card</h6>
            <Link to="/" className="mini_quest">
              Why do we need this?
            </Link>
            <CardElement options={options} onChange={handleCardDetailsChange} />
            {cardError && <p className="validation_error">{cardError}</p>}
          </div>
        )}

        {paymentMethod === "apple_pay" && !isFreeTrial && (
          <div className="blk_form date_ex_flex">
            <h6>Apple Pay</h6>
            <ApplePayButton
              stripe={stripe}
              plan={plan}
              planId={plan_id}
              postData={{
                ...formData,
                action: searchParams.get("action"),
              }}
              appliedCoupon={appliedCoupon}
              disabled={paymentBusy}
              validateBeforePayment={validateApplePayPayment}
              saveSubscription={chargeApplePayPayment}
              onStart={() => {
                setCardError(null);
                setCardLoading(true);
              }}
              onFinish={() => setCardLoading(false)}
              onError={setCardError}
              onCouponError={(message) =>
                setFormErrors((prev) => ({
                  ...prev,
                  coupon_code: message,
                }))
              }
              isCouponError={isCouponError}
              formatCouponMessage={formatPromoCodeMessage}
              onAvailabilityChange={setApplePayAvailable}
            />
            {applePayAvailable === null && (
              <p className="validation-error" style={{ color: "#061237" }}>
                Checking Apple Pay availability...
              </p>
            )}
            {applePayAvailable === false && (
              <p className="validation-error">
                Apple Pay is not available on this browser, device, or domain.
                Use Safari with Apple Pay enabled, or choose Credit / Debit Card.
              </p>
            )}
            {cardError && <p className="validation_error">{cardError}</p>}
          </div>
        )}

        {/* --- Billing Address Section --- */}
        <h6 className="checkout_sm_heading">Billing address</h6>
        <div className="blk_form">
          <h6>
            {paymentMethod === "ach"
              ? "Account holder name"
              : paymentMethod === "apple_pay"
              ? "Billing name"
              : "Card holder name"}
          </h6>
          <div className="form_blk">
            <input
              type="text"
              name="name_on_card"
              className="input"
              placeholder="Name"
              value={formData.name_on_card}
              onChange={handleInputChange}
            />
            {formErrors.name_on_card && (
              <span className="validation-error">{formErrors.name_on_card}</span>
            )}
          </div>
        </div>

        <div className="blk_form">
          <h6>Address</h6>
          <div className="form_blk">
            <input
              type="text"
              name="address"
              className="input"
              placeholder="Address"
              value={formData.address}
              onChange={handleInputChange}
            />
            {formErrors.address && (
              <span className="validation-error">{formErrors.address}</span>
            )}
          </div>
        </div>

        <div className="blk_form">
          <h6>City</h6>
          <div className="form_blk">
            <input
              type="text"
              name="city"
              className="input"
              placeholder="City"
              value={formData.city}
              onChange={handleInputChange}
            />
            {formErrors.city && (
              <span className="validation-error">{formErrors.city}</span>
            )}
          </div>
        </div>

        <div className="flex form_flex_half">
          <div className="blk_form">
            <h6>State</h6>
            <div className="form_blk">
              <input
                type="text"
                name="state"
                className="input"
                placeholder="State"
                value={formData.state}
                onChange={handleInputChange}
              />
              {formErrors.state && (
                <span className="validation-error">{formErrors.state}</span>
              )}
            </div>
          </div>
          <div className="blk_form">
            <h6>Zip code</h6>
            <div className="form_blk">
              <input
                type="text"
                name="zip_code"
                className="input"
                placeholder="Zip code"
                value={formData.zip_code}
                onChange={handleInputChange}
              />
              {formErrors.zip_code && (
                <span className="validation-error">{formErrors.zip_code}</span>
              )}
            </div>
          </div>
        </div>

        <div className="line_br"></div>

        {paymentMethod === "ach" && achStep !== "idle" && (
          <p
            className="validation-error"
            style={{ color: achStep === "error" ? undefined : "#061237" }}
          >
            {achStep === "collecting" && "Opening bank account selector..."}
            {achStep === "confirming" && "Confirming bank authorization..."}
            {achStep === "pending" &&
              "Payment submitted. Activation takes 2-4 business days."}
            {achStep === "error" && achError}
          </p>
        )}

        {/* --- Submission Button Logic --- */}
        <div className="btn_blk">
          {isTermsAccepted ? (
            paymentMethod === "apple_pay" && !isFreeTrial ? (
              <p className="validation-error" style={{ color: "#061237" }}>
                Use the Apple Pay button above to complete checkout.
              </p>
            ) : (
              <SubmitButton
                type="submit" // Ensure button inside form has type="submit"
                button_text={
                  searchParams.get("action")
                    ? "Subscribe"
                    : content.left_sec_button_text_1
                }
                classes="site_btn"
                disabled={paymentBusy}
                isFormProcessing={
                  !stripe || !elements || cardLoading || isFormProcessing
                }
              />
            )
          ) : (
            <button
              type="button" // Use type="button" to prevent form submission
              className="site_btn"
              onClick={handleProceedToTerms}
            >
              {searchParams.get("action")
                ? "Subscribe"
                : content.left_sec_button_text_1}
            </button>
          )}
        </div>

      </form>

      {/* --- Terms and Conditions Popup --- */}
      {termsPopup && (
        <TermsPopup
          terms_conditions={terms_conditions}
          subscription_agreement={subscription_agreement}
          setTermsPopup={setTermsPopup}
          setIsTermsAccepted={setIsTermsAccepted}
          isTermsAccepted={isTermsAccepted}
        />
      )}
    </>
  );
};

export default Form;
